/** Release 2 S4 (A) — the sidebar's "System boards" list with live counts (`listSystemBoards`, refreshed by WS). */
import { useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { ChevronRight, CircleDashed, Clock, Copy, Eye, FolderX, TagIcon } from 'lucide-react';
import { useSystemBoards } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { writeLS } from '@/stores/ui';
import { cx } from '@/lib/format';
import { SYSTEM_BOARD_HINT, SYSTEM_BOARD_LABEL } from './definition';

const ICON: Record<string, typeof TagIcon> = { untagged: TagIcon, incomplete: CircleDashed, unfiled: FolderX, needs_review: Eye, expiring: Clock, duplicates: Copy };
const OPEN_KEY = 'sidebar:systemBoardsOpen';
function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function SystemBoardsSection({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { ws = '' } = useParams();
  const wsId = useSessionStore((s) => s.workspace?.id);
  const boards = useSystemBoards(wsId);
  const [open, setOpen] = useState(readOpen);
  if (!boards.data?.length) return null;
  const toggle = () => {
    setOpen(!open);
    writeLS(OPEN_KEY, !open);
  };
  return (
    <section className="mt-2 px-2" aria-label="System boards" data-system-boards>
      {!collapsed && (
        <button type="button" onClick={toggle} aria-expanded={open} className="flex w-full items-center gap-1 px-1 py-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase hover:text-fg">
          <ChevronRight size={12} className={cx('transition-transform', open && 'rotate-90')} /> System boards
        </button>
      )}
      {(open || collapsed) && (
        <ul className="space-y-px">
          {boards.data.map((b) => {
            const Icon = ICON[b.key] ?? CircleDashed;
            const label = SYSTEM_BOARD_LABEL[b.key] ?? b.name;
            const to = b.link ? `/w/${ws}${b.link}` : `/w/${ws}/system/${b.key}`;
            return (
              <li key={b.key}>
                <NavLink to={to} onClick={onNavigate} title={SYSTEM_BOARD_HINT[b.key] ?? label} data-system-board={b.key} className={({ isActive }) => cx('flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-bg-sunken', isActive && 'bg-bg-sunken font-medium text-accent', collapsed && 'justify-center px-0')}>
                  <Icon size={14} className="shrink-0" />
                  {!collapsed && <span className="truncate">{label}</span>}
                  {!collapsed && b.count != null && <span className={cx('ml-auto shrink-0 text-xs tabular-nums', b.count ? 'text-fg-subtle' : 'text-fg-subtle/60')} data-count>{b.count.toLocaleString()}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
